/**
 * @Description 集客点录入抽屉
 */

import V2Drawer from '@/common/components/Feedback/V2Drawer';
import styles from './index.module.less';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Form from '@/common/components/Form/V2Form';
import { Button, Col, Form, Row, Space } from 'antd';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EnvironmentOutlined } from '@ant-design/icons';
import V2FormCascader from '@/common/components/Form/V2FormCascader/V2FormCascader';
import V2Tabs from '@/common/components/Data/V2Tabs';
import IconFont from '@/common/components/IconFont';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import { V2Confirm } from '@/common/components/Others/V2Confirm';
import { isNotEmptyAny, isNumber, refactorSelection } from '@lhb/func';
import { getTreeSelection } from '@/common/api/networkplan';
import {
  approvalReboot,
  createApproval,
} from '@/common/api/expandStore/approveworkbench';
import {
  ApprovalType,
  ApprovalTypeValue,
} from '@/common/components/business/ExpandStore/ts-config';
import TabForm from './TabForm';
import DrawingBoard from './DrawingBoard';
import { gatherClientStatus } from './ts-config';
import { batchSave, getDetail } from '@/common/api/sitemapgather';
import { MUNICIPALITY_AND_SAR } from '@/common/utils/map';
import UserInfoContext from '@/layout/context';

export enum EnterDrawerTypeEnums {
  Quick = 'quick',
  Normal = 'normal'
}


export interface EnterDrawerStateType{
  visible: boolean;
  value: {id: string};
  type?: EnterDrawerTypeEnums;
}

interface EnterDrawerProps {
  open: EnterDrawerStateType;
  setOpen: any;
  onRefresh?: ()=>void;
  onQuicklyCreateCb?: (data:any)=>void;
}

const EnterDrawer:FC<EnterDrawerProps> = ({ open, setOpen, onRefresh, onQuicklyCreateCb }) => {
  // console.log('open', open, JSON.stringify(open));
  const [form] = Form.useForm();
  const [mapIns, setMapIns] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [activeKey, setActiveKey] = useState<string>('0');
  const [lineInfo, setLineInfo] = useState<any>(null);
  const [relationDetail, setRelationDetail] = useState<any>({});
  const [districtTypeOptions, setDistrictTypeOptions] = useState<any>([]);
  const [tableFormLength, setTableFormLength] = useState<any>({});
  const tableFormLengthRef = useRef<any>(null);
  const mapInsRef = useRef<any>(null);
  const defaultTabItem = {
    key: '0',
    label: '集客点1',
  };
  const tabItemsRef = useRef<any>([defaultTabItem]);

  const lineInfoRef = useRef<any>(null);
  const hasQuicklyCreateAddRef = useRef<boolean>(false); // 快速传教是否新增过集客点
  // const newTabIndex = useRef(2);
  const [tabItems, setTabItems] = useState<any>([defaultTabItem]);
  // const tabKeys = ['firstTabKey', 'secondTabKey', 'thirdTabKey'];

  /** 是否快速创建 */
  const isQuicklyCreate = useMemo(() => open?.type === EnterDrawerTypeEnums.Quick, [open?.type]);
  const userInfo: any = useContext(UserInfoContext); // 获取用户信息
  // console.log('tabItems', tabItems);
  const creatorId = useMemo(() => {
    if (isQuicklyCreate) {
      return userInfo?.id;
    }
    return null;
  }, [userInfo, isQuicklyCreate]);

  /** 如果是快速创建的话，则需要id和地图数据 */
  const drawingBoardValue = useMemo(() => {
    return open?.type === EnterDrawerTypeEnums.Quick ? { ...relationDetail, id: open.value?.id } : open?.value;
  }, [open, relationDetail]);

  const renderAddBtn = () => {
    if (isQuicklyCreate) {
      //  未新增过集客点 && 集客点数量为0 就不展示
      return !!relationDetail?.planSpots?.length && !hasQuicklyCreateAddRef.current;
    }
    return true;
  };

  const saveBtnDisabled = () => {
    // 如果 relationDetail 或 planSpots 不存在，按钮应启用
    if (!relationDetail || !relationDetail.planSpots) {
      return false;
    }

    // 如果 planSpots 存在但为空数组，按钮应启用
    if (relationDetail.planSpots.length === 0) {
      return false;
    }

    // 如果有集客点，看是否新增按钮应禁用
    return !hasQuicklyCreateAddRef.current;
  };

  // /** 表单是否可以编辑 */
  // const formDisabled = useMemo(() => {
  //   const isPlanSpotsDefined = relationDetail?.planSpots?.length > 0;
  //   const isLastTabActive = activeKey === String(tabItems.length - 1);

  //   let formDisabled = false;

  //   if (isPlanSpotsDefined) {
  //     if (hasQuicklyCreateAddRef.current) {
  //       formDisabled = !isLastTabActive;
  //     } else {
  //       formDisabled = true;
  //     }
  //   }
  //   return formDisabled;
  // }, [hasQuicklyCreateAddRef.current, activeKey]);



  useEffect(() => {
    if (open.visible && open.value?.id) {
      form.resetFields();
      setActiveKey('0');
      setTableFormLength({});
      getRelationDetail();
    }
  }, [open]);

  useEffect(() => {
    getDistrictTypeOptions();
  }, []);

  /* methods */
  /**
   * @description 获取集客点详情
   */
  const getRelationDetail = () => {
    getDetail({
      modelClusterId: open.value?.id,
    }).then((data) => {
      setRelationDetail(data);
      const resetForm: any = {
        businessType: [],
      };
      // 重组form表单数据
      const newTabItems: any = [];
      const initTableFormLen: any = {};
      if (data.planSpots?.length) {
        if (data.firstLevelCategory) {
          resetForm.businessType.push(data.firstLevelCategory);
        }
        if (data.secondLevelCategory) {
          resetForm.businessType.push(data.secondLevelCategory);
        }
        if (data.thirdLevelCategory) {
          resetForm.businessType.push(data.thirdLevelCategory);
        }
        data.planSpots.map((item, index) => {
          initTableFormLen[String(index)] = {};
          if (item.competitors?.length) {
            // 填充是否选择竞品
            item.isCompare = true;
            const competitors = {};
            item.competitors.map((itm, index) => {
              competitors[`${index}`] = itm;
            });
            initTableFormLen[String(index)]['competitors'] =
              item.competitors.length;
            item.competitors = competitors;
          } else {
            // 填充是否选择竞品
            item.isCompare = false;
          }
          if (item.landlords) {
            const landlords = {};
            item.landlords.map((itm, index) => {
              landlords[`${index}`] = itm;
            });
            initTableFormLen[String(index)]['landlords'] =
              item.landlords.length;
            item.landlords = landlords;
          }
          resetForm[String(index)] = item;
          newTabItems.push({
            key: String(index),
            label: `集客点${index + 1}`,
          });
          if (!initTableFormLen[String(index)]['competitors']) {
            initTableFormLen[String(index)]['competitors'] = 1;
          }
          if (!initTableFormLen[String(index)]['landlords']) {
            initTableFormLen[String(index)]['landlords'] = 1;
          }
        });
      } else {
        // 未提交则默认添加一个集客点
        resetForm['0'] = null;
        newTabItems.push({
          key: '0',
          label: `集客点1`,
        });
        initTableFormLen['0'] = {
          competitors: 1,
          landlords: 1,
        };
      }
      setTableFormLength(initTableFormLen);
      tableFormLengthRef.current = initTableFormLen;
      tabItemsRef.current.splice(0, tabItemsRef.current.length, ...newTabItems);
      form.setFieldsValue(resetForm);
      refactorTabItems();
    });
  };

  const getDistrictTypeOptions = () => {
    getTreeSelection({ type: 3 }).then((data) => {
      const options = refactorSelection(data, { children: 'child' });
      setDistrictTypeOptions(options);
    });
  };

  const onChange = (key: any) => {
    setActiveKey(key);
  };
  // 添加集客点tab
  const addTabItemHandle = () => {
    if (isQuicklyCreate && hasQuicklyCreateAddRef.current) {
      V2Message.warning('快速创建只允许新增一个集客点，请勿重复添加');
    } else {
      const newActiveKey = String(tabItemsRef.current.length);
      tabItemsRef.current.push({
        key: newActiveKey,
        label: `集客点${tabItemsRef.current.length + 1}`,
      });
      tableFormLengthRef.current[newActiveKey] = {
        landlords: 1,
        competitors: 1,
      };
      setTableFormLength({ ...tableFormLengthRef.current });
      refactorTabItems();
      setTimeout(() => {
        setActiveKey(newActiveKey);
      }, 0);
      if (isQuicklyCreate && !hasQuicklyCreateAddRef.current) {
        hasQuicklyCreateAddRef.current = true;
      }
    }
  };
  // 添加集客点tab(ps：这里会存在闭包问题，拿不到最新的state)
  const deleteItemHandle = (targetKey: string) => {
    if (tabItemsRef.current.length === 1) {
      V2Message.warning('至少保留一个集客点');
      return;
    }
    V2Confirm({
      content: '确认删除此集客点吗？',
      onOk: () => {
        const targetIndex = tabItemsRef.current.findIndex(
          (pane) => pane.key === targetKey
        );
        tabItemsRef.current.splice(targetIndex, 1);
        const { key } = targetIndex === tabItemsRef.current.length ? tabItemsRef.current[targetIndex - 1] : { key: '0' };
        setActiveKey(key);
        tableFormLengthRef.current[targetKey] = null;
        setTableFormLength({ ...tableFormLengthRef.current });
        form.setFieldsValue({ [targetKey]: null });
        refactorTabItems();

        // 清除该tab下面的集客点线段
        lineInfoRef.current?.[targetKey]?.marker?.map((item) => {
          item && mapInsRef.current?.remove(item);
        });
        setLineInfo({
          ...lineInfoRef.current,
          [targetKey]: null,
        });
      },
    });
  };

  // 重组tabItems
  const refactorTabItems = () => {
    const newTabItem = tabItemsRef.current.map((item, index) => {
      return {
        key: String(index),
        label: (
          <span>
            {`集客点${index + 1}`}
            {!isQuicklyCreate && <IconFont
              iconHref='iconic-closexhdpi'
              className='ml-6 fs-14 c-869'
              onClick={(e) => {
                e.stopPropagation();
                deleteItemHandle(String(index));
              }}
            />}
          </span>
        ),
      };
    });
    tabItemsRef.current = newTabItem;
    setTabItems(newTabItem);
  };
  // console.log('tabItemsRef.current', tabItemsRef.current, tabItems);
  const onSubmit = () => {
    form
      .validateFields()
      .then((values: any) => {
        const firstLevelCategory = values.businessType[0]; // 商圈一级类目
        const secondLevelCategory = values.businessType[1]; // 商圈二级类目
        const thirdLevelCategory = values.businessType[2]; // 商圈三级类目
        const planSpots: any = [];
        // 遍历拿到子集相关值
        for (const spot in values) {
          if (isNumber(Number(spot))) {
            // 如果id是空字符串，那么转成undefined
            if (!isNotEmptyAny(values[spot]?.id)) {
              values[spot].id = undefined;
            }
            if (isNotEmptyAny(values[spot]?.competitors)) {
              const competitors: any = [];
              // eslint-disable-next-line guard-for-in
              for (const comp in values[spot].competitors) {
                competitors.push(values[spot].competitors[comp]);
              }
              values[spot].competitors = competitors;
            }
            if (isNotEmptyAny(values[spot]?.landlords)) {
              const landlords: any = [];
              // eslint-disable-next-line guard-for-in
              for (const land in values[spot].landlords) {
                landlords.push(values[spot].landlords[land]);
              }
              values[spot].landlords = landlords;
            }
            if (!lineInfo?.[spot]?.marker) {
              V2Message.error('请在所有集客点中绘制动线');
              return;
            }
            values[spot].moveLineList = lineInfo?.[spot]?.marker?.map(
              (marker) => {
                return marker.getPath().map((item) => ({
                  lng: item.lng,
                  lat: item.lat,
                }));
              }
            );
            planSpots.push(values[spot]);
            delete values[spot];
          }
        }
        if (isQuicklyCreate) {
          const _planSpots = planSpots.filter((item) => !item?.id).map((item:any) => ({ ...item, firstLevelCategory, secondLevelCategory, thirdLevelCategory, creatorId }));
          const params = {
            modelClusterId: relationDetail.modelClusterId,
            ...values,
            firstLevelCategory,
            secondLevelCategory,
            thirdLevelCategory,
            planSpots: _planSpots,
            planSpotCount: _planSpots.length,
          };
          onQuicklyCreateCb?.(params);
          onCancel();
          V2Message.success('创建成功');
          setLoading(false);
          return;
        }
        const params = {
          modelClusterId: relationDetail.modelClusterId,
          ...values,
          firstLevelCategory,
          secondLevelCategory,
          thirdLevelCategory,
          planSpots,
          planSpotCount: planSpots.length,
        };
        setLoading(true);
        // https://yapi.lanhanba.com/project/546/interface/api/80261
        batchSave(params)
          .then(() => {
            if (
              relationDetail.approvalId &&
              relationDetail.spotStatus === gatherClientStatus.DENY
            ) {
              // 已驳回时才能重新发起
              approvalReboot({ id: relationDetail.approvalId })
                .then(() => {
                  // 暂时不支持-添加自选节点
                  V2Message.success(`重新发起成功`);
                  onRefresh?.();
                  onCancel();
                })
                .finally(() => setLoading(false));
            } else {
              createApproval({
                // 暂时不支持-添加自选节点
                type: ApprovalType.PlanSpot,
                typeValue: ApprovalTypeValue.SiteMapGatherClient,
                id: relationDetail.modelClusterId,
              })
                .then(() => {
                  V2Message.success('提交成功');
                  onRefresh?.();
                  onCancel();
                })
                .finally(() => setLoading(false));
            }
          })
          .finally(() => setLoading(false));
      })
      .catch((err) => {
        V2Message.warning(err.errorFields[0].errors[0]);
      });
  };
  const onSave = () => {
    const values = form.getFieldsValue();
    const firstLevelCategory = values.businessType[0]; // 商圈一级类目
    const secondLevelCategory = values.businessType[1]; // 商圈二级类目
    const thirdLevelCategory = values.businessType[2]; // 商圈三级类目
    const planSpots: any = [];
    // 遍历拿到子集相关值
    for (const spot in values) {
      if (isNumber(Number(spot))) {
        // 如果id是空字符串，那么转成undefined
        if (!isNotEmptyAny(values[spot]?.id)) {
          values[spot].id = undefined;
        }
        if (isNotEmptyAny(values[spot]?.competitors)) {
          const competitors: any = [];
          // eslint-disable-next-line guard-for-in
          for (const comp in values[spot].competitors) {
            competitors.push(values[spot].competitors[comp]);
          }
          values[spot].competitors = competitors;
        }
        if (isNotEmptyAny(values[spot]?.landlords)) {
          const landlords: any = [];
          // eslint-disable-next-line guard-for-in
          for (const land in values[spot].landlords) {
            landlords.push(values[spot].landlords[land]);
          }
          values[spot].landlords = landlords;
        }
        if (!lineInfo?.[spot]?.marker) {
          V2Message.error('请在所有集客点中绘制动线');
          return;
        }
        values[spot].moveLineList = lineInfo?.[spot]?.marker?.map(
          (marker) => {
            return marker.getPath().map((item) => ({
              lng: item.lng,
              lat: item.lat,
            }));
          }
        );
        planSpots.push(values[spot]);
        delete values[spot];
      }
    }
    const params = {
      modelClusterId: relationDetail.modelClusterId,
      ...values,
      firstLevelCategory,
      secondLevelCategory,
      thirdLevelCategory,
      planSpots,
      planSpotCount: planSpots.length,
    };
    // 判断集客点名称和是否有竞品 有没有填写
    const isNotName = planSpots.find((item) => !item?.name);
    const isNotIsCompare = planSpots.find((item) => typeof item?.isCompare !== 'boolean');
    if (!values?.businessType) {
      V2Message.error('请填写商圈类型后保存');
      return;
    }
    if (isNotName) {
      V2Message.error('请填写集客点名称后保存');
      return;
    }
    if (isNotIsCompare) {
      V2Message.error('请选择是否有竞品后保存');
      return;
    }
    setLoading(true);
    // https://yapi.lanhanba.com/project/546/interface/api/80261
    batchSave(params).finally(() => {
      setLoading(false);
      V2Message.success('保存成功');
      onRefresh?.();
      onCancel();
    });
  };

  const onCancel = () => {
    form.resetFields();
    setOpen({
      visible: false,
      value: null,
    });
    setLineInfo(null);
    hasQuicklyCreateAddRef.current = false;
    // 关闭时，清除覆盖物
    mapIns?.clearMap();
    // 关闭时，清除所画动线（按理说有了上面的，就不需要清除下面的逻辑）
    // Object.values(lineInfo || {}).map((item:any) => {
    //   item?.marker?.map((m) => {
    //     m && mapIns.remove(m);
    //   });
    // });
  };

  const renderTabForm = () => {
    return tabItems?.map((tabKey) => {
      const isPlanSpotsDefined = relationDetail?.planSpots?.length > 0;
      const isLastTabActive = activeKey === String(tabItems.length - 1);

      let formDisabled = false;

      if (isQuicklyCreate && isPlanSpotsDefined) {
        if (hasQuicklyCreateAddRef.current) {
          formDisabled = !isLastTabActive;
        } else {
          formDisabled = true;
        }
      }
      return <div className={activeKey === tabKey.key ? styles.showTab : styles.hindTab} key={tabKey.key}>
        <TabForm
          keyName={tabKey.key}
          tableFormLength={tableFormLength}
          setTableFormLength={setTableFormLength}
          formDisabled={formDisabled}
        />
      </div>;
    });
  };

  useEffect(() => {
    lineInfoRef.current = lineInfo;
  }, [lineInfo]);
  useEffect(() => {
    mapInsRef.current = mapIns;
  }, [mapIns]);
  return (
    <V2Drawer
      className={styles.enterDrawerWrap}
      open={open.visible}
      onClose={onCancel}
      forceRender={false}
      bodyStyle={{ paddingBottom: '88px' }}
      keyboard={false}
      contentWrapperStyle={{
        width: '1008px',
      }}
    >
      <V2Title type='H1' text={relationDetail.name} />
      <Space size={24} className={styles.drawerTop}>
        <div className={styles.topItem}>
          <EnvironmentOutlined className='c-666' />
          <span className='ml-4 c-666 fs-13'>
            {/* 不显示直辖市名称，会和city重复 */}
            {MUNICIPALITY_AND_SAR.find((item: any) => relationDetail.province?.includes(item)) ? '' : relationDetail.province}
            {relationDetail.city || ''}
            {relationDetail.district || ''}
          </span>
        </div>
        <div className={styles.topItem}>
          <span className='c-999 fs-14'>已开门店数</span>
          <span className='c-222 ml-4 fs-14'>
            {relationDetail.openStores ? `${relationDetail.openStores}家` : 0}
          </span>
        </div>
      </Space>
      <V2Form form={form} className='mt-24'>
        <Row gutter={24}>
          <Col span={8}>
            <V2FormCascader
              required
              label='商圈类型'
              name='businessType'
              disabled={isQuicklyCreate && !!relationDetail?.planSpots?.length } // 如果是快速创建，这里会带入值，并且商圈类型不可编辑
              options={districtTypeOptions}
              config={{ showSearch: true }}
            />
          </Col>
        </Row>
        <div className={styles.tabs}>
          <V2Tabs
            items={tabItems}
            onChange={onChange}
            activeKey={activeKey}
            tabBarExtraContent={{
              right: (
                <>
                  {
                    renderAddBtn() && <div
                      className={styles.tabItemAddBtn}
                      onClick={addTabItemHandle}
                    >
                      <IconFont iconHref='iconic_add_xiangqing' />
                      <span className='c-222 fs-14 ml-4'>新增集客点</span>
                    </div>
                  }
                </>
              ),
            }}
          />
          {renderTabForm()}
          {/* 集客点动线始末- ui要求不同tabs下面的地图一致，所以通过在tabs内容中留白，DrawingBoard绝对定位嵌入进去 */}
          <DrawingBoard
            tabItems={tabItems}
            keyName={activeKey}
            setLineInfo={setLineInfo}
            lineInfo={lineInfo}
            mapIns={mapIns}
            setMapIns={setMapIns}
            relationDetail={relationDetail}
            value={drawingBoardValue}
            visible={open?.visible}
          />
        </div>
      </V2Form>
      <div className={styles.drawerFooter}>
        <Space size={12}>
          <Button onClick={onCancel}>取消</Button>
          {!isQuicklyCreate ? <>
            <Button type='primary' loading={loading} onClick={onSave}>
            保存
            </Button>
            <Button type='primary' loading={loading} onClick={onSubmit}>
            提交
            </Button>
          </> : <Button type='primary' loading={loading} onClick={onSubmit} disabled={ saveBtnDisabled()}>
          保存
          </Button>}
        </Space>
      </div>
    </V2Drawer>
  );
};

export default EnterDrawer;
